/** The receipt, as the thing a reviewer is being asked to attest.
 *
 * A before-and-after pair of numbers says what changed, not whether the figure is sound. What a
 * reviewer signs is the chain behind the figure: the document it came from, the value read, the
 * factor that priced it, the method, the verifier and the ledger entry. This draws that chain
 * compactly - one line per link, each a **word and a glyph**, a missing link with the server's
 * reason - and the figure and its inputs above it. It is the same data the receipt drawer reads
 * (`buildChain` is the one decision about each link), so the two cannot disagree; the full
 * arithmetic stays in the drawer (rule 3's single rendering) and this opens it.
 *
 * **A figure whose chain has a break is not dressed as ready to sign.** The summary line counts the
 * links and names how many are missing; it does not say the figure may be approved, because that
 * is the reviewer's judgement and the server's separate refusals (an open blocking flag, stage
 * order, separation of the submitter).
 */

import { CheckCircle, MinusCircle, XCircle } from "@phosphor-icons/react";

import type { Receipt } from "../api/queries";
import { factorValue, quantity, shortDate, tonnes, unitLabel } from "../lib/format";
import { KeyFacts } from "./ui/KeyFacts";
import { StatusChip } from "./ui/StatusChip";
import { buildChain, summarise } from "./receiptLinks";
import type { Standing } from "./receiptLinks";
import styles from "./ReceiptAttest.module.css";

const WORD: Record<Standing, { word: string; tone: "ok" | "neutral" | "crit"; icon: typeof CheckCircle }> = {
  present: { word: "Present", tone: "ok", icon: CheckCircle },
  not_applicable: { word: "Not applicable", tone: "neutral", icon: MinusCircle },
  missing: { word: "Missing", tone: "crit", icon: XCircle },
};

export function ReceiptAttest({
  receipt,
  onOpenReceipt,
}: {
  receipt: Receipt;
  onOpenReceipt: () => void;
}) {
  const nodes = buildChain(receipt);
  const counts = summarise(nodes);
  const factor = receipt.factor;
  const factorLine = factor.value
    ? `${factorValue(factor.value)} ${factor.unit_out ?? ""} ${factor.publisher ? `· ${factor.publisher}` : ""}${factor.edition ? ` ${factor.edition}` : ""}`.trim()
    : "None applied";

  return (
    <div className={styles.attest} data-testid="attest-receipt">
      <p className={styles.figure}>
        <span className={`${styles.value} display`} data-testid="attest-figure">
          {tonnes(receipt.figure_kgco2e)}
        </span>
        <span className={styles.unit}>tCO₂e</span>
        <span className={styles.scope}>
          Scope {receipt.scope} · {receipt.category.replace(/_/g, " ")}
        </span>
      </p>
      {!receipt.is_current ? (
        <p className={styles.superseded} role="status" data-testid="attest-superseded">
          This figure has been replaced by a later one. What you decide here is about the version
          you were asked to attest.
        </p>
      ) : null}

      <KeyFacts
        as="h3"
        layout="inline"
        facts={[
          {
            label: "Activity",
            value: `${quantity(receipt.activity_quantity, 1)} ${unitLabel(receipt.activity_unit)}`,
          },
          {
            label: "Period",
            value: `${shortDate(receipt.activity_period_start)} to ${shortDate(receipt.activity_period_end)}`,
          },
          { label: "Site", value: receipt.facility_name },
          { label: "Factor", value: factorLine },
          { label: "Method", value: receipt.method ? receipt.method.replace(/_/g, " ") : "Not stated" },
          {
            label: "Source document",
            value: receipt.source_document
              ? (receipt.source_document.original_filename ?? receipt.source_document.kind.replace(/_/g, " "))
              : receipt.reference_kind === "stated_basis"
                ? "None: a stated basis"
                : "None",
          },
        ]}
      />

      <h3 className={styles.chainHead}>
        The chain behind it{" "}
        <span className={styles.chainCount} data-testid="attest-chain-summary">
          {counts.present} of {counts.total} links present
          {counts.missing > 0 ? `, ${counts.missing} missing` : ""}
        </span>
      </h3>
      <ol className={styles.chain} data-testid="attest-chain">
        {nodes.map((node) => {
          const word = WORD[node.standing];
          return (
            <li key={node.key} className={styles.link} data-standing={node.standing}>
              <StatusChip tone={word.tone} size="sm" icon={word.icon}>
                {word.word}
              </StatusChip>
              <span className={styles.label}>{node.label}</span>
              {node.reason ? <span className={styles.reason}>{node.reason}</span> : null}
            </li>
          );
        })}
      </ol>
      <button type="button" className={styles.open} onClick={onOpenReceipt} data-testid="attest-open-receipt">
        Open the full receipt
      </button>
    </div>
  );
}
